// ============================================================================
// useWorkflowChecklist — derives an ordered "checklist" of workflow states for
// an instance, marking each as done / current / future based on history.
// ============================================================================
// Combines two backend calls:
//   1. workflowService.getInstance(instanceId)             → current state + history
//   2. workflowService.getDefinitionFull(instance.defId)   → full ordered states list
// and produces a flat ChecklistStep[] sorted by displayOrder.
//
// Consumers: <WorkflowChecklistRail>, modal-level stepContext computation.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { workflowService } from '../api/workflowService';
import type { DefinitionFullDetail, WorkflowInstanceDetail, WorkflowState } from '../api/types';

export type ChecklistStepStatus = 'done' | 'current' | 'future';

export interface ChecklistStep {
  state: WorkflowState;
  status: ChecklistStepStatus;
  /** Most recent history entry where this state became the to_state. */
  enteredAt?: string;
  enteredBy?: string;
}

/**
 * A Stage is a grouping of one or more workflow states sharing the same
 * stage_code. The rail UI renders one card per stage and treats the stage
 * as "completed" once any of its inner states becomes a terminal/done step.
 */
export interface ChecklistStage {
  stageCode: string;
  stageLabel: string;
  stageIcon?: string;
  displayOrder: number;
  steps: ChecklistStep[];
  /** done = workflow has moved beyond any of the stage's states;
   *  current = one of the stage's states is the current workflow state;
   *  future = no state in this stage has been entered yet. */
  status: ChecklistStepStatus;
  /** Number of inner steps with status done|current. */
  doneCount: number;
  totalCount: number;
}

export interface ChecklistData {
  instanceId: string;
  definitionId: string;
  definitionCode?: string;
  currentStateId: string;
  currentStateCode?: string;
  /** Flat list of all steps in workflow order — still useful for legacy consumers. */
  steps: ChecklistStep[];
  /** Stage groupings derived from state.stageCode. Empty array when the
   *  workflow has no stages assigned (older definitions). */
  stages: ChecklistStage[];
  /** Done + current = "where we've gotten to". */
  doneCount: number;
  totalCount: number;
}

export const useWorkflowChecklist = (instanceId?: string | null) => {
  const [instance, setInstance] = useState<WorkflowInstanceDetail | null>(null);
  const [definition, setDefinition] = useState<DefinitionFullDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!instanceId) {
      setInstance(null);
      setDefinition(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const inst = await workflowService.getInstance(instanceId);
      setInstance(inst);
      const def = await workflowService.getDefinitionFull(inst.instance.definitionId);
      setDefinition(def);
    } catch (err) {
      // Surface the real cause to the console so developers can debug 404s,
      // permission denials, etc. The rail also shows error.message inline.
      // eslint-disable-next-line no-console
      console.error('[useWorkflowChecklist] fetch failed for instance', instanceId, err);
      setError(err as Error);
      setInstance(null);
      setDefinition(null);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const data = useMemo<ChecklistData | null>(() => {
    if (!instance || !definition || !instanceId) return null;

    const states = [...definition.states].sort((a, b) => a.displayOrder - b.displayOrder);
    const currentStateId = instance.instance.currentStateId;
    const currentStateCode =
      instance.instance.currentStateCode ??
      states.find(s => s.id === currentStateId)?.stateCode;

    // Set of state IDs we've ever transitioned INTO (history.toStateId).
    const enteredStateIds = new Set<string>();
    const lastEntryByState = new Map<string, { firedAt: string; actor?: string | null }>();
    for (const h of instance.history) {
      enteredStateIds.add(h.toStateId);
      lastEntryByState.set(h.toStateId, { firedAt: h.firedAt, actor: h.actorUserName });
    }

    const steps: ChecklistStep[] = states.map(state => {
      let status: ChecklistStepStatus;
      if (state.id === currentStateId) status = 'current';
      else if (enteredStateIds.has(state.id)) status = 'done';
      else status = 'future';
      const last = lastEntryByState.get(state.id);
      return {
        state,
        status,
        enteredAt: last?.firedAt,
        enteredBy: last?.actor ?? undefined,
      };
    });

    const doneCount = steps.filter(s => s.status === 'done' || s.status === 'current').length;

    // Group by stage_code (when defined on the states). States without a
    // stage_code render as their own "stages" (one step per card) to keep
    // backwards-compat with workflow definitions that haven't been tagged.
    const stageMap = new Map<string, ChecklistStage>();
    for (const step of steps) {
      const stageCode = step.state.stageCode ?? `__state_${step.state.stateCode}`;
      const stageLabel = step.state.stageLabel ?? step.state.displayName;
      const stageIcon = step.state.stageIcon ?? undefined;
      const stageDisplayOrder = step.state.stageDisplayOrder ?? step.state.displayOrder;

      let stage = stageMap.get(stageCode);
      if (!stage) {
        stage = {
          stageCode,
          stageLabel,
          stageIcon,
          displayOrder: stageDisplayOrder,
          steps: [],
          status: 'future',
          doneCount: 0,
          totalCount: 0,
        };
        stageMap.set(stageCode, stage);
      }
      stage.steps.push(step);
    }

    // Drop "summary-only" stages from the rail — stages whose every inner
    // state has no form fields/sections at all (typically the workflow's
    // START state seeded as an empty placeholder, e.g. DRAFT_BASIC_DETAILS).
    // Case identity belongs in the sidebar's Overview > Summary, not in a
    // rail step that says "nothing to do here, fire next action".
    const isStageSummaryOnly = (stage: ChecklistStage): boolean =>
      stage.steps.every((step) => {
        const spec: any = (step.state as any).formSpec;
        if (!spec) return true;
        const hasFlatFields = Array.isArray(spec.fields) && spec.fields.length > 0;
        const hasSections   = Array.isArray(spec.sections) && spec.sections.length > 0;
        return !hasFlatFields && !hasSections;
      });

    const stages = Array.from(stageMap.values())
      .filter((stage) => !isStageSummaryOnly(stage))
      .map((stage) => {
        const total = stage.steps.length;
        const hasCurrent = stage.steps.some((s) => s.status === 'current');
        const hasDone = stage.steps.some((s) => s.status === 'done');
        const status: ChecklistStepStatus = hasCurrent ? 'current' : hasDone ? 'done' : 'future';
        const doneInStage = stage.steps.filter((s) => s.status === 'done' || s.status === 'current').length;
        return { ...stage, status, doneCount: doneInStage, totalCount: total };
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);

    return {
      instanceId,
      definitionId: definition.definition.id,
      definitionCode: definition.definition.definitionCode,
      currentStateId,
      currentStateCode: currentStateCode ?? undefined,
      steps,
      stages,
      doneCount,
      totalCount: steps.length,
    };
  }, [instance, definition, instanceId]);

  return { data, loading, error, refetch: fetch };
};
